import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Download, Globe, Mail, Gamepad2, Calendar, Monitor, Tag, MapPin,
  Building2, Play, ExternalLink, Loader2, FileText, Quote, Award, Video,
} from "lucide-react";
import { Button } from "../components/ui/button";
import HlsPlayer from "../components/HlsPlayer";
import Lightbox from "../components/Lightbox";
import BrandLogo from "../components/BrandLogo";
import { useTheme } from "../context/ThemeContext";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GamePressKit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const [pk, setPk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/games/${id}/presskit`, { timeout: 25000 })
      .then((res) => { if (active) setPk(res.data); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5fbfc] dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (notFound || !pk) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#f5fbfc] dark:bg-slate-950">
        <p className="font-display font-700 text-2xl text-slate-900 dark:text-white">Press kit not available</p>
        <Link to={`/game/${id}`} className="text-teal-600 font-semibold">← Back to game</Link>
      </div>
    );
  }

  const accent = pk.accent || "#0E7FA8";
  const trailer = pk.videos && pk.videos[0];
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${pk.downloadUrl}`;
  const assetUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  return (
    <div className="min-h-screen bg-[#f5fbfc] dark:bg-slate-950 transition-colors">
      {/* Top bar */}
      <div className="fixed top-0 inset-x-0 z-40 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-cyan-100 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo className="w-8 h-8" />
            <span className="font-display font-800 text-base sm:text-lg text-slate-900 dark:text-white">Sharkbowl Games</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggle} aria-label="Toggle theme" className="w-9 h-9 rounded-full flex items-center justify-center bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-white/80">
              {dark ? "☀" : "☾"}
            </button>
            <button onClick={() => navigate(`/game/${id}`)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-white/80 hover:text-teal-600">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back to game</span>
            </button>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="pt-16">
        <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}22, transparent)` }}>
          <div className="max-w-7xl mx-auto px-6 py-12">
            <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" style={{ background: `${accent}22`, color: accent }}>
              Press Kit
            </span>
            <h1 className="font-display font-800 text-4xl sm:text-6xl text-slate-900 dark:text-white break-words">{pk.title}</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-2">{pk.genre} · {pk.factsheet.developer}</p>
            <a href={downloadUrl} data-testid="presskit-download-all" onClick={() => trackEvent("file_download", { game_id: id, kit_type: "all", file_name: `${id}-presskit.zip` })}>
              <Button className="mt-6 h-12 rounded-full text-white font-semibold gap-2 hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                <Download className="w-5 h-5" /> Download all assets (.zip)
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-12">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-12">
          {pk.description && (
            <Section title="Description">
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{pk.description}</p>
            </Section>
          )}

          {pk.history && (
            <Section title="History">
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{pk.history}</p>
            </Section>
          )}

          {pk.features && pk.features.length > 0 && (
            <Section title="Features">
              <ul className="space-y-2">
                {pk.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                    <span className="text-base">{f}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {trailer && (trailer.hls || trailer.youtubeId || trailer.mp4) && (
            <Section title="Videos">
              <button onClick={() => setShowTrailer(true)} className="group relative w-full aspect-video rounded-2xl overflow-hidden shadow-xl bg-slate-800" data-testid="presskit-trailer">
                <img src={trailer.thumbnail || pk.headerImage} alt="trailer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                  <span className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-9 h-9 text-teal-600 ml-1" fill="currentColor" />
                  </span>
                </div>
                <span className="absolute bottom-4 left-4 text-white font-semibold bg-black/40 px-3 py-1 rounded-full text-sm">{trailer.name}</span>
              </button>
            </Section>
          )}

          {pk.images && pk.images.length > 0 && (
            <Section title="Images">
              <div className="grid sm:grid-cols-2 gap-4">
                {pk.images.map((s, i) => (
                  <button key={i} onClick={() => setLightboxIndex(i)} className="aspect-video rounded-xl overflow-hidden group bg-slate-200 dark:bg-slate-800 cursor-pointer" data-testid={`presskit-image-${i}`}>
                    <img src={s} alt={`${pk.title} screenshot ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-400 mt-3">All images are included in the downloadable press kit above.</p>
            </Section>
          )}

          {pk.logos && (pk.logos.gameLogo || pk.logos.studioLogo) && (
            <Section title="Logo & Icon">
              <a href={`${process.env.REACT_APP_BACKEND_URL}${pk.logos.downloadUrl}`} data-testid="presskit-download-logo" onClick={() => trackEvent("file_download", { game_id: id, kit_type: "logo", file_name: `${id}-logo.zip` })} className="inline-flex items-center gap-2 text-sm font-semibold mb-5 hover:underline" style={{ color: accent }}>
                <Download className="w-4 h-4" /> Download logo files (.zip)
              </a>
              <div className="flex flex-wrap items-end gap-6">
                {pk.logos.gameLogo && (
                  <div className="flex flex-col items-center gap-2" data-testid="presskit-logo-game">
                    <div className="rounded-2xl bg-[#0f172a] border border-white/10 p-6 flex items-center justify-center min-h-[140px] min-w-[200px]">
                      <img src={assetUrl(pk.logos.gameLogo)} alt={`${pk.title} logo`} className="max-h-24 max-w-[220px] object-contain" onError={(e) => { e.currentTarget.closest('[data-testid="presskit-logo-game"]').style.display = 'none'; }} />
                    </div>
                    <span className="text-xs text-slate-400">Game logo</span>
                  </div>
                )}
                <div className="flex flex-col items-center gap-2" data-testid="presskit-logo-studio">
                  <div className="rounded-2xl bg-[#e2e8f0] border border-slate-200 dark:border-white/10 p-6 flex items-center justify-center min-h-[140px] min-w-[200px]">
                    <img src={assetUrl(pk.logos.studioLogo)} alt="Sharkbowl Games logo" className="max-h-24 max-w-[220px] object-contain" />
                  </div>
                  <span className="text-xs text-slate-400">Studio logo</span>
                </div>
                {pk.logos.icon && (
                  <div className="flex flex-col items-center gap-2" data-testid="presskit-logo-icon">
                    <div className="rounded-2xl bg-[#e2e8f0] border border-slate-200 dark:border-white/10 p-6 flex items-center justify-center min-h-[140px] min-w-[140px]">
                      <img src={assetUrl(pk.logos.icon)} alt="Sharkbowl icon" className="w-20 h-20 object-contain rounded-2xl" />
                    </div>
                    <span className="text-xs text-slate-400">Icon</span>
                  </div>
                )}
              </div>
            </Section>
          )}

          {pk.pressArt && pk.pressArt.length > 0 && (
            <Section title="Key Art & Capsules">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Promotional artwork and store capsules. Click any asset to open it full size. Everything is also in the downloadable press kit above.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pk.pressArt.map((a, i) => (
                  <a
                    key={i}
                    href={assetUrl(a.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`presskit-art-${i}`}
                    className="group rounded-2xl overflow-hidden bg-[#0f172a] border border-white/10 hover:border-white/30 transition-colors"
                  >
                    <div className="aspect-video flex items-center justify-center p-3 bg-[repeating-conic-gradient(#1e293b_0_25%,#0f172a_0_50%)] bg-[length:24px_24px]">
                      <img src={assetUrl(a.url)} alt={a.label} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-sm font-semibold text-slate-200">{a.label}</span>
                      <Download className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                    </div>
                  </a>
                ))}
              </div>
            </Section>
          )}


          {pk.awards && pk.awards.length > 0 && (
            <Section title="Awards & Recognition">
              <ul className="space-y-2">
                {pk.awards.map((a, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                    <Award className="w-5 h-5 text-amber-500 shrink-0" /> {a}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {pk.quotes && pk.quotes.length > 0 && (
            <Section title="Selected Articles">
              <div className="space-y-4">
                {pk.quotes.map((q, i) => (
                  <blockquote key={i} className="rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-5" data-testid={`presskit-article-${i}`}>
                    <Quote className="w-5 h-5 text-teal-400 mb-2" />
                    <p className="text-slate-700 dark:text-slate-200 italic">"{q.text}"</p>
                    {(q.source || q.outlet) && (
                      <p className="text-sm text-slate-400 mt-2">
                        — {q.source}
                        {q.url ? (
                          <a href={q.url} target="_blank" rel="noopener noreferrer" className="ml-1 font-semibold text-teal-600 dark:text-cyan-300 hover:underline inline-flex items-center gap-1">
                            {q.outlet || "Read article"} <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          q.outlet && <span className="ml-1 font-semibold text-slate-500 dark:text-slate-300">{q.outlet}</span>
                        )}
                      </p>
                    )}
                  </blockquote>
                ))}
              </div>
            </Section>
          )}

          {pk.monetization && (
            <Section title="Monetization Permission">
              <div className="rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-6 flex items-start gap-4" data-testid="presskit-monetization">
                <span className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${accent}1a`, color: accent }}>
                  <Video className="w-5 h-5" />
                </span>
                <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">{pk.monetization}</p>
              </div>
            </Section>
          )}

          {pk.credits && pk.credits.length > 0 && (
            <Section title="Credits">
              <ul className="space-y-2">
                {pk.credits.map((c, i) => (
                  <li key={i} className="flex justify-between text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-white/5 pb-2">
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-slate-400">{c.role}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>

        {/* Sidebar: Factsheet + Contact */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <a href={downloadUrl} data-testid="presskit-download-side" onClick={() => trackEvent("file_download", { game_id: id, kit_type: "all", file_name: `${id}-presskit.zip` })}>
              <Button className="w-full h-12 rounded-full text-white font-semibold gap-2" style={{ background: accent }}>
                <Download className="w-5 h-5" /> Download press kit
              </Button>
            </a>

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-6 shadow-[0_10px_40px_rgba(14,127,168,0.08)]">
              <h3 className="font-display font-800 text-lg text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: accent }} /> Factsheet
              </h3>
              <div className="space-y-3 text-sm">
                <Row icon={Building2} label="Developer" value={pk.factsheet.developer} />
                <Row icon={MapPin} label="Based in" value={pk.factsheet.basedIn} />
                <Row icon={Calendar} label="Release" value={pk.factsheet.releaseDate} />
                <Row icon={Gamepad2} label="Genre" value={pk.factsheet.genre} />
                {pk.factsheet.platforms && pk.factsheet.platforms.length > 0 && (
                  <Row icon={Monitor} label="Platforms" value={pk.factsheet.platforms.join(", ")} />
                )}
                <Row icon={Tag} label="Price" value={pk.factsheet.price} />
              </div>
            </div>

            {pk.additionalLinks && pk.additionalLinks.length > 0 && (
              <div className="rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-6">
                <h3 className="font-display font-800 text-lg text-slate-900 dark:text-white mb-3">Links</h3>
                <div className="space-y-2">
                  {pk.additionalLinks.map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-teal-600 dark:text-cyan-300 hover:underline text-sm">
                      <ExternalLink className="w-4 h-4" /> {l.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-6">
              <h3 className="font-display font-800 text-lg text-slate-900 dark:text-white mb-3">Contact</h3>
              <a href={`mailto:${pk.contact.press}`} className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-teal-600 text-sm mb-2">
                <Mail className="w-4 h-4" /> {pk.contact.press}
              </a>
              <a href={pk.contact.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-teal-600 text-sm mb-3">
                <Globe className="w-4 h-4" /> sharkbowlgames.com
              </a>
              <div className="flex flex-wrap gap-2">
                {pk.contact.socials.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-white/10">
                    {s.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {showTrailer && trailer && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={() => setShowTrailer(false)}>
          <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            {trailer.hls ? (
              <HlsPlayer src={trailer.hls} poster={trailer.thumbnail} className="w-full h-full" />
            ) : trailer.mp4 ? (
              <video
                src={`${process.env.REACT_APP_BACKEND_URL}${trailer.mp4}`}
                poster={trailer.thumbnail ? `${process.env.REACT_APP_BACKEND_URL}${trailer.thumbnail}` : undefined}
                controls
                autoPlay
                playsInline
                className="w-full h-full rounded-2xl"
              />
            ) : (
              <iframe
                className="w-full h-full rounded-2xl"
                src={`https://www.youtube-nocookie.com/embed/${trailer.youtubeId}?autoplay=1&rel=0`}
                title={trailer.name || "Trailer"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox images={pk.images} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-800 text-2xl text-slate-900 dark:text-white mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 dark:border-white/5 pb-3 last:border-0 last:pb-0">
      <span className="flex items-center gap-2 text-slate-400 dark:text-slate-500 shrink-0">
        <Icon className="w-4 h-4" /> {label}
      </span>
      <span className="font-semibold text-slate-800 dark:text-white text-right">{value}</span>
    </div>
  );
}
